import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { CustomerDataQualityProvider } from '@components/UserGroupDataQuality/CustomerDataQualityProvider';
import { UserGroupDataQualityPromptCustomer } from '@components/UserGroupDataQuality/UserGroupDataQualityPromptCustomer';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingLocationType } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { ScreenBaseWrapper } from 'noddi-ui-web';
import { BookingDetailsTabs, getJobCompletedAndPaymentLeft } from 'noddi-util';
import { DepartmentOrganizationName } from 'noddi-web';
import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLocalBrandFromOrg } from '../../hooks/useLocalBrandFromOrg';
import { getIsVisitLive, SERVICE_DAY_REFETCH_INTERVAL_MS } from '../../utils/bookingVisit';
import { BSlugContent } from './BSlugContent';
import { BookingUpdateOfferSection } from './BookingUpdateOfferSection/BookingUpdateOfferSection';

export const BSlug = () => {
  const { slug } = useParams() as { slug: string };
  const [searchParams] = useSearchParams();
  const paymentButtonRef = useRef<HTMLDivElement | null>(null);

  const {
    data: booking,
    isPending: isBookingPending,
    isFetching: isBookingFetching,
    isFetchedAfterMount: isBookingFetchedAfterMount,
    error: bookingError,
    refetch: refetchBooking
  } = noddiAsync.useGet({
    type: URLKeys.getBookingInfo,
    input: { slug },
    queryConfig: {
      // A tab reopened on the service day refetches, which re-evaluates the interval below.
      refetchOnWindowFocus: true,
      // The timeline follows the visit on the service day without a reload.
      refetchInterval: (query) =>
        query.state.data && getIsVisitLive(query.state.data) ? SERVICE_DAY_REFETCH_INTERVAL_MS : false
    }
  });

  // Localhost has no brand hostname, so adopt the organization brand the
  // same way a proposal does. The prompt below uses the booking's own brand.
  useLocalBrandFromOrg({ serviceOrganizationId: booking?.serviceOrganizationId });

  const isStationary = booking?.locationType === BookingLocationType.STATIONARY;
  const currentTab = searchParams.get('tab') as BookingDetailsTabs | null;

  const defaultTab =
    isStationary && (!currentTab || currentTab === BookingDetailsTabs.STATUS_UPDATES)
      ? BookingDetailsTabs.ORDER_DETAILS
      : (currentTab ?? undefined);

  // Auto-scroll to payment button when component mounts and payment is required
  useEffect(() => {
    if (!booking) {
      return;
    }

    const shouldScroll = getJobCompletedAndPaymentLeft(booking);
    if (!shouldScroll || !paymentButtonRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      paymentButtonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [booking]);

  // Payment callback will change status on booking so make sure to also listen to fetching here.
  // Only until the first fetch after mount: a poll swaps the booking in place, no loader.
  if (isBookingPending || (isBookingFetching && !isBookingFetchedAfterMount)) {
    return <NoddiLoadingScreenWithTrans />;
  }

  // Past the first load, no booking means it failed; a failed poll keeps the page and its drafts.
  if (!booking) {
    return <ApiErrorPageWithTrans error={[bookingError]} />;
  }

  const isOrganization = !booking.userGroup.isPersonal;

  return (
    <CustomerDataQualityProvider
      userGroupId={booking.userGroup.id}
      bookingLocationType={booking.locationType}
      brandDomain={booking.serviceDepartmentBrand.domain}
      countryCode={booking.address?.countryCode}
      // Bookings made by the shop never pass our checkout; this page
      // is often the only time the customer can fix the invoice email/address.
      openWhenBlocked
    >
      <ScreenBaseWrapper width='lg'>
        <div className='flex flex-col gap-5'>
          <div className='flex flex-col gap-2 text-balance'>
            <h1 className='text-3xl font-bold tracking-tight text-primary md:text-4xl'>
              {t`Hi`} {booking.user.firstName}!
            </h1>
            {isOrganization && (
              <p className='flex items-start gap-1.5 text-sm text-muted-foreground'>
                <NoddiIcon name='Building2' size='small' />
                <span>
                  <Trans>On behalf of</Trans>
                </span>
                {booking.customerDepartmentName ? (
                  <DepartmentOrganizationName
                    departmentName={booking.customerDepartmentName}
                    organizationName={booking.customerOrganizationName}
                  />
                ) : (
                  booking.userGroup.name
                )}
              </p>
            )}
          </div>
          {bookingError && (
            <ApiErrorMessageWithTrans
              error={bookingError}
              onRetry={() => void refetchBooking()}
              isRetrying={isBookingFetching}
            />
          )}
          <UserGroupDataQualityPromptCustomer />
          <BookingUpdateOfferSection booking={booking} />
          <BSlugContent booking={booking} defaultTab={defaultTab} paymentButtonRef={paymentButtonRef} />
        </div>
      </ScreenBaseWrapper>
    </CustomerDataQualityProvider>
  );
};
